import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { barrattAPI, type ImportTemplate, type SupplierUploadDatasetType } from '../../../lib/api/barratt';
import { useFetchData } from '../../../hooks/useFetchData';
import { DATASET_ORDER, TEMPLATE_ID_BY_DATASET, downloadTemplateCsv } from '../uploadCycle';

export interface UploadTemplatesResult {
  /** The two templates this cycle asks for, in display order. */
  templates: { dataset: SupplierUploadDatasetType; template: ImportTemplate }[];
  byDataset: Partial<Record<SupplierUploadDatasetType, ImportTemplate>>;
  loading: boolean;
  error: string | null;
  retry: () => void;
  /** Saves that dataset's header row as a CSV. */
  download: (dataset: SupplierUploadDatasetType) => void;
}

/**
 * The Supplier Performance and Quality templates, so a supplier can see the
 * columns expected of them and start from the right header row.
 *
 * Same source as the customer-side uploader (GET import-summaries/templates/),
 * so neither side can drift from the columns the importer validates.
 *
 * @returns The two templates, their load state, and the download handler
 */
export function useUploadTemplates(): UploadTemplatesResult {
  const query = useFetchData<ImportTemplate[]>(async () => {
    const { data } = await barrattAPI.getImportTemplates();
    return data.templates ?? [];
  });

  const byDataset = useMemo(() => {
    const all = query.data ?? [];
    const found: Partial<Record<SupplierUploadDatasetType, ImportTemplate>> = {};
    for (const dataset of DATASET_ORDER) {
      const match = all.find((t) => t.id === TEMPLATE_ID_BY_DATASET[dataset]);
      if (match) found[dataset] = match;
    }
    return found;
  }, [query.data]);

  const templates = useMemo(
    () =>
      DATASET_ORDER.flatMap((dataset) => {
        const template = byDataset[dataset];
        return template ? [{ dataset, template }] : [];
      }),
    [byDataset],
  );

  const download = useCallback(
    (dataset: SupplierUploadDatasetType) => {
      const template = byDataset[dataset];
      if (!template) {
        toast.error('That template is not available yet — try refreshing.');
        return;
      }
      toast.success(`${downloadTemplateCsv(template)} downloaded`);
    },
    [byDataset],
  );

  const { refetch } = query;
  return {
    templates,
    byDataset,
    loading: query.loading,
    error: query.error,
    retry: () => void refetch(),
    download,
  };
}
